/**
 * Autocomplete de localização para notícias (cidade, estado, região, país derivados do cadastro de cidades).
 * GET /api/admin/localizacoes/search?q=… — consulta Dynamo em tempo real quando disponível.
 * Fallback: catálogo já hidratado no painel (`getCidades()`).
 */

import { adminIngestApiUrl } from './adminIngestApi';
import { adminApiHeaders } from './adminApiAuth';
import { getCidades, type Cidade } from './experienciasStorage';
import type { NewsLocalizacaoEscopo } from './newsStorage';

export type NoticiaLocalizacaoPick =
  | {
      kind: 'cidade';
      cityId: string;
      label: string;
      pais: string;
      estado: string;
      regiao: string;
    }
  | { kind: 'pais'; label: string; pais: string }
  | { kind: 'estado'; label: string; pais: string; estado: string }
  | { kind: 'regiao'; label: string; pais: string; estado: string; regiao: string };

const SEARCH_TIMEOUT_MS = 12_000;
const MAX_RESULTS = 48;

function normalizePickRow(raw: unknown): NoticiaLocalizacaoPick | null {
  if (!raw || typeof raw !== 'object' || !('kind' in raw)) return null;
  const o = raw as Record<string, unknown>;
  const kind = o.kind;
  const label = typeof o.label === 'string' ? o.label.trim() : '';
  if (!label) return null;
  if (kind === 'cidade' && typeof o.cityId === 'string') {
    return {
      kind: 'cidade',
      cityId: o.cityId.trim(),
      label,
      pais: typeof o.pais === 'string' ? o.pais.trim() : '',
      estado: typeof o.estado === 'string' ? o.estado.trim() : '',
      regiao: typeof o.regiao === 'string' ? o.regiao.trim() : '',
    };
  }
  if (kind === 'pais' && typeof o.pais === 'string') {
    return { kind: 'pais', label, pais: o.pais.trim() };
  }
  if (kind === 'estado' && typeof o.pais === 'string' && typeof o.estado === 'string') {
    return { kind: 'estado', label, pais: o.pais.trim(), estado: o.estado.trim() };
  }
  if (
    kind === 'regiao' &&
    typeof o.pais === 'string' &&
    typeof o.estado === 'string' &&
    typeof o.regiao === 'string'
  ) {
    return {
      kind: 'regiao',
      label,
      pais: o.pais.trim(),
      estado: o.estado.trim(),
      regiao: o.regiao.trim(),
    };
  }
  return null;
}

export function buildNoticiaLocalizacaoPicks(cidades: Cidade[], query: string): NoticiaLocalizacaoPick[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const picks: NoticiaLocalizacaoPick[] = [];
  const seen = new Set<string>();

  const push = (p: NoticiaLocalizacaoPick) => {
    const key =
      p.kind === 'cidade'
        ? `c:${p.cityId}`
        : p.kind === 'pais'
          ? `p:${p.pais}`
          : p.kind === 'estado'
            ? `e:${p.pais}\t${p.estado}`
            : `r:${p.pais}\t${p.estado}\t${p.regiao}`;
    if (seen.has(key)) return;
    seen.add(key);
    picks.push(p);
  };

  for (const c of cidades) {
    const nome = (c.cidade || '').trim();
    const estado = (c.estadoProvincia || '').trim();
    const pais = (c.pais || '').trim();
    const hay = `${nome} ${estado} ${pais} ${(c.regioes || []).join(' ')} ${c.cep}`.toLowerCase();
    if (!hay.includes(q)) continue;
    push({
      kind: 'cidade',
      cityId: c.id,
      label: `${nome}${estado ? `, ${estado}` : ''}${pais ? ` — ${pais}` : ''}`,
      pais,
      estado,
      regiao: (c.regioes?.[0] || '').trim(),
    });
  }

  const paises = new Set<string>();
  const estados = new Map<string, { pais: string; estado: string }>();
  const regioes = new Map<string, { pais: string; estado: string; regiao: string }>();

  for (const c of cidades) {
    const p = (c.pais || '').trim();
    const e = (c.estadoProvincia || '').trim();
    if (p) paises.add(p);
    if (p && e) estados.set(`${p}\t${e}`, { pais: p, estado: e });
    for (const r of c.regioes || []) {
      const rr = (r || '').trim();
      if (p && e && rr) regioes.set(`${p}\t${e}\t${rr}`, { pais: p, estado: e, regiao: rr });
    }
  }

  for (const p of [...paises].sort((a, b) => a.localeCompare(b, 'pt-BR'))) {
    if (!p.toLowerCase().includes(q)) continue;
    push({ kind: 'pais', label: p, pais: p });
  }

  for (const x of [...estados.values()].sort((a, b) =>
    `${a.estado} ${a.pais}`.localeCompare(`${b.estado} ${b.pais}`, 'pt-BR')
  )) {
    if (!`${x.pais} ${x.estado}`.toLowerCase().includes(q)) continue;
    push({ kind: 'estado', label: `${x.estado} — ${x.pais}`, pais: x.pais, estado: x.estado });
  }

  for (const x of [...regioes.values()].sort((a, b) =>
    `${a.regiao} ${a.estado}`.localeCompare(`${b.regiao} ${b.estado}`, 'pt-BR')
  )) {
    if (!`${x.pais} ${x.estado} ${x.regiao}`.toLowerCase().includes(q)) continue;
    push({
      kind: 'regiao',
      label: `${x.regiao} — ${x.estado}, ${x.pais}`,
      pais: x.pais,
      estado: x.estado,
      regiao: x.regiao,
    });
  }

  return picks.slice(0, MAX_RESULTS);
}

export function formPatchFromNoticiaLocalizacaoPick(
  pick: NoticiaLocalizacaoPick
): {
  localizacaoEscopo: NewsLocalizacaoEscopo;
  cityId: string;
  localizacaoPais: string;
  localizacaoEstado: string;
  localizacaoRegiao: string;
} {
  switch (pick.kind) {
    case 'cidade':
      return {
        localizacaoEscopo: 'cidade',
        cityId: pick.cityId,
        localizacaoPais: pick.pais,
        localizacaoEstado: pick.estado,
        localizacaoRegiao: pick.regiao,
      };
    case 'pais':
      return {
        localizacaoEscopo: 'pais',
        cityId: '',
        localizacaoPais: pick.pais,
        localizacaoEstado: '',
        localizacaoRegiao: '',
      };
    case 'estado':
      return {
        localizacaoEscopo: 'estado',
        cityId: '',
        localizacaoPais: pick.pais,
        localizacaoEstado: pick.estado,
        localizacaoRegiao: '',
      };
    case 'regiao':
      return {
        localizacaoEscopo: 'regiao',
        cityId: '',
        localizacaoPais: pick.pais,
        localizacaoEstado: pick.estado,
        localizacaoRegiao: pick.regiao,
      };
  }
}

export const EMPTY_NOTICIA_LOCALIZACAO_FORM = {
  localizacaoEscopo: 'global' as const,
  cityId: '',
  localizacaoPais: '',
  localizacaoEstado: '',
  localizacaoRegiao: '',
};

/** Busca na API; em 404/erro usa cidades do catálogo local (sincronizado com Dynamo). */
export async function searchNoticiaLocalizacoes(
  query: string,
  signal?: AbortSignal
): Promise<NoticiaLocalizacaoPick[]> {
  const q = query.trim();
  if (!q) return [];

  const controller = new AbortController();
  const linked = signal
    ? (() => {
        if (signal.aborted) controller.abort();
        else signal.addEventListener('abort', () => controller.abort(), { once: true });
        return controller.signal;
      })()
    : controller.signal;
  const kill = setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS);

  try {
    const url = adminIngestApiUrl(
      `/api/admin/localizacoes/search?q=${encodeURIComponent(q)}&limit=${MAX_RESULTS}`
    );
    const res = await fetch(url, { method: 'GET', headers: adminApiHeaders(), signal: linked });
    if (res.ok) {
      const data: unknown = await res.json();
      if (Array.isArray(data)) {
        const rows = data.map(normalizePickRow).filter((x): x is NoticiaLocalizacaoPick => x != null);
        if (rows.length > 0) return rows.slice(0, MAX_RESULTS);
      }
    }
  } catch {
    /* rede ou endpoint ainda não publicado */
  } finally {
    clearTimeout(kill);
  }

  return buildNoticiaLocalizacaoPicks(getCidades(), q);
}

export function labelLocalizacaoResumo(
  escopo: NewsLocalizacaoEscopo,
  form: {
    cityId: string;
    localizacaoPais: string;
    localizacaoEstado: string;
    localizacaoRegiao: string;
  },
  cidades: Cidade[]
): string {
  if (escopo === 'global') return 'Global';
  if (escopo === 'pais') return form.localizacaoPais.trim() || '—';
  if (escopo === 'estado')
    return [form.localizacaoEstado, form.localizacaoPais].filter(Boolean).join(' — ') || '—';
  if (escopo === 'regiao')
    return [form.localizacaoRegiao, form.localizacaoEstado, form.localizacaoPais].filter(Boolean).join(' — ') || '—';
  const c = cidades.find((x) => x.id === form.cityId);
  return c ? `${c.cidade}${c.estadoProvincia ? `, ${c.estadoProvincia}` : ''}` : '—';
}
